'use strict';
// No separate cart UI, prices or pixels: use the same drawer and Shopify cart events as the shop.
(function () {
  const sdkReady = import('https://cdn.shopify.com/storefront/standard-events.js').catch(() => null);
  const pendingKey = 'lelafine:advent:pending-cart-add';
  let busy = false;
  const readPending = () => { try { return JSON.parse(sessionStorage.getItem(pendingKey)); } catch (_) { return null; } };
  const savePending = value => { try { value ? sessionStorage.setItem(pendingKey, JSON.stringify(value)) : sessionStorage.removeItem(pendingKey); } catch (_) {} };
  const matches = (item, line) => String(item.variant_id) === String(line.id) && item.properties?._advent_group === line.properties._advent_group;
  const emit = (name, detail) => document.documentElement.dispatchEvent(new CustomEvent(name, {bubbles: true, detail}));
  const message = (dk, de) => window.ADVENT_MARKET?.country === 'DK' ? dk : de;
  const cartUrl = path => (window.Shopify?.routes?.root || '/') + path;
  async function json(response, fallback) {
    let body;
    try { body = await response.json(); } catch (_) { throw new Error(fallback); }
    if (!response.ok) throw new Error(body.description || body.message || fallback);
    return body;
  }
  async function readCart() {
    const snapshot = window.adventTakeCartSnapshot?.();
    return snapshot || json(await fetch(cartUrl('cart.js'), {headers: {Accept: 'application/json'}}), message('Kurven kan ikke hentes. Prøv igen.', 'Der Warenkorb konnte nicht geladen werden. Bitte versuche es erneut.'));
  }
  async function prepareEvent(lines) {
    let timer;
    try {
      const sdk = await Promise.race([sdkReady, new Promise(resolve => { timer = setTimeout(() => resolve(null), 500); })]);
      if (!sdk) return null;
      const deferred = sdk.CartLinesUpdateEvent.createPromise();
      deferred.promise.catch(() => {});
      document.body.dispatchEvent(new sdk.CartLinesUpdateEvent({action: 'add', context: 'page', lines: lines.map(line => ({merchandiseId: String(line.id), quantity: line.quantity})), promise: deferred.promise}));
      return {sdk, deferred};
    } finally { clearTimeout(timer); }
  }
  window.adventAddToNativeCart = async function (lines, attributes) {
    if (busy) return;
    busy = true;
    let eventReady = Promise.resolve(null), addConfirmed = false, addedItems = [];
    try {
      // Upgrade the existing drawer before requesting its bundled section.
      let drawerTimer;
      try {
        await Promise.race([customElements.whenDefined('cart-drawer'), new Promise((_resolve, reject) => {
          drawerTimer = setTimeout(() => reject(new Error(message('Kurven er ikke klar. Genindlæs siden og prøv igen.', 'Der Warenkorb ist noch nicht bereit. Lade die Seite neu.'))), 6000);
        })]);
      } finally { clearTimeout(drawerTimer); }
      const sections = [];
      emit('cart:prepare-bundled-sections', {sections});
      const drawer = document.getElementById('cart-drawer');
      if (!drawer || !sections.length) throw new Error(message('Kurven er ikke klar. Genindlæs siden og prøv igen.', 'Der Warenkorb ist noch nicht bereit. Lade die Seite neu.'));
      const current = await readCart();
      const signature = JSON.stringify(lines.map(line => [line.id, line.quantity]));
      let pending = readPending();
      if (!pending || pending.signature !== signature) {
        pending = {signature, lines: lines.map(line => {
          const existing = current.items.find(item => String(item.variant_id) === String(line.id) && item.properties?._advent_2026 === 'true' && item.properties?._advent_group);
          return {...line, properties: {...line.properties, _advent_group: existing?.properties?._advent_group || line.properties._advent_group}, targetQuantity: (existing?.quantity || 0) + line.quantity};
        })};
        // Retain the intended final quantity after a lost add/update response, including reloads.
        savePending(pending);
      }
      const missing = pending.lines.flatMap(line => {
        const existing = current.items.find(item => matches(item, line));
        const quantity = line.targetQuantity - (existing?.quantity || 0);
        return quantity > 0 ? [{id: line.id, quantity, properties: line.properties}] : [];
      });
      if (missing.length) {
        eventReady = prepareEvent(missing).catch(() => null);
        const response = await fetch(cartUrl('cart/add.js'), {method: 'POST', headers: {'Content-Type': 'application/json', Accept: 'application/json'}, body: JSON.stringify({items: missing})});
        if (!response.ok) {
          // A definite rejection did not add stock. A transport failure retains the retry target.
          savePending(null);
          await json(response, message('Kalenderen kunne ikke lægges i kurven.', 'Der Kalender konnte nicht hinzugefügt werden.'));
        }
        addConfirmed = true;
        const added = await response.json();
        addedItems = (Array.isArray(added.items) ? added.items : [added]).filter(item => missing.some(line => matches(item, line)));
      }
      // Add stock only through add.js. Attribute-only update cannot change/remove existing items.
      // Its authoritative full cart also supplies the normal drawer without an extra cart GET.
      const fullCart = await json(await fetch(cartUrl('cart/update.js'), {
        method: 'POST', headers: {'Content-Type': 'application/json', Accept: 'application/json'},
        body: JSON.stringify({attributes, sections: [...new Set(sections)].slice(0, 5), sections_url: location.pathname})
      }), message('Kalenderen ligger i kurven. Prøv igen for at vise kurven.', 'Der Kalender liegt im Warenkorb. Versuche es erneut, um den Warenkorb zu öffnen.'));
      if (sections.some(id => !fullCart.sections?.[id])) throw new Error(message('Kalenderen ligger i kurven. Prøv igen for at vise kurven.', 'Der Kalender liegt im Warenkorb. Versuche es erneut, um den Warenkorb zu öffnen.'));
      emit('cart:change', {baseEvent: 'variant:add', cart: fullCart});
      emit('variant:add', {items: addedItems.length ? addedItems : fullCart.items.filter(item => pending.lines.some(line => matches(item, line))), cart: fullCart});
      // Opening is immediate; slow analytics must not hold the visible cart closed.
      eventReady.then(event => { if (event) event.deferred.resolve({cart: event.sdk.CartLinesUpdateEvent.createCartFromAjaxResponse(fullCart)}); });
      savePending(null);
      return fullCart;
    } catch (error) {
      eventReady.then(event => {
        if (!event) return;
        if (addConfirmed) event.deferred.resolve({cart: null});
        else { document.body.dispatchEvent(new event.sdk.CartErrorEvent({error: error.message, code: 'SERVICE_UNAVAILABLE'})); event.deferred.reject(error); }
      });
      throw error;
    } finally { busy = false; }
  };
})();
